import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ConcreteVolumeCalculator = () => {
  const [concreteData, setConcreteData] = useState({
    length: '',
    width: '',
    depth: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConcreteData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateVolume = () => {
    const { length, width, depth } = concreteData;
    
    const len = parseFloat(length) || 0;
    const wid = parseFloat(width) || 0;
    const dep = parseFloat(depth) || 0;

    if (len <= 0 || isNaN(len)) {
      setError('Please enter a valid length');
      return;
    }

    if (wid <= 0 || isNaN(wid)) {
      setError('Please enter a valid width');
      return;
    }

    if (dep <= 0 || isNaN(dep)) {
      setError('Please enter a valid depth');
      return;
    }

    // Calculate volume
    const volume = len * wid * dep;

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Length: ${len} meters`,
      `Width: ${wid} meters`,
      `Depth: ${dep} meters`,
      '\nStep 2: Calculate volume',
      `Volume = Length × Width × Depth`,
      `Volume = ${len} × ${wid} × ${dep} = ${volume.toFixed(2)} cubic meters`
    ];

    const newResults = {
      volume: volume.toFixed(2),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Volume'],
        datasets: [{
          label: 'Volume (cubic meters)',
          data: [volume],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2
        }]
      }
    });
  };

  const exampleData = {
    length: 5,
    width: 4,
    depth: 0.3,
    volume: 6.00
  };

  return (
    <>
     <Helmet>
      {/* Basic SEO Meta Tags */}
      <title>Concrete Volume Calculator - Construction Estimation</title>
      <meta name="description" content="Calculate the volume of concrete required for construction projects. Use our interactive concrete volume calculator to estimate the amount of concrete needed." />
      <meta name="keywords" content="concrete volume calculator, construction estimation, volume calculation, concrete calculation, construction projects" />
      <meta name="author" content="Your Company Name" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Open Graph Meta Tags for Facebook and LinkedIn */}
      <meta property="og:title" content="Concrete Volume Calculator - Construction Estimation" />
      <meta property="og:description" content="Calculate the volume of concrete required for construction projects. Use our interactive concrete volume calculator to estimate the amount of concrete needed." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yourdomain.com/concrete-volume-calculator" />
      <meta property="og:image" content="https://yourdomain.com/concrete-volume-calculator-preview.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="Construction Calculators" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@your_twitter_handle" />
      <meta name="twitter:creator" content="@your_twitter_handle" />
      <meta name="twitter:title" content="Concrete Volume Calculator - Construction Estimation" />
      <meta name="twitter:description" content="Calculate the volume of concrete required for construction projects. Use our interactive concrete volume calculator to estimate the amount of concrete needed." />
      <meta name="twitter:image" content="https://yourdomain.com/concrete-volume-calculator-preview.jpg" />
      <meta name="twitter:image:alt" content="Concrete Volume Calculator Preview" />
      
      {/* Pinterest Meta Tags */}
      <meta name="pinterest" content="nopin" />
      <meta name="pinterest-rich-pin" content="true" />
      
      {/* Schema.org Markup for Google+ */}
      <meta itemprop="name" content="Concrete Volume Calculator - Construction Estimation" />
      <meta itemprop="description" content="Calculate the volume of concrete required for construction projects. Use our interactive concrete volume calculator to estimate the amount of concrete needed." />
      <meta itemprop="image" content="https://yourdomain.com/concrete-volume-calculator-preview.jpg" />
      
      {/* Additional Meta Tags for Better Sharing */}
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Concrete Volume Calculator" />
    </Helmet>

    <div className="concrete-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🏗️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Concrete Volume Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Construction Estimation</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate the volume of concrete required for construction projects. This calculator helps you estimate the amount of concrete needed for your project.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="length">
            Length (meters):
          </label>
          <input
            type="number"
            id="length"
            name="length"
            value={concreteData.length}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter length in meters"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="width">
            Width (meters):
          </label>
          <input
            type="number"
            id="width"
            name="width"
            value={concreteData.width}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter width in meters"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="depth">
            Depth (meters):
          </label>
          <input
            type="number"
            id="depth"
            name="depth"
            value={concreteData.depth}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter depth in meters"
          />
        </div>

        <button
          onClick={calculateVolume}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Volume
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Concrete Volume</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Volume (cubic meters)</span>
                    <span className="font-medium">{results.volume}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Volume (cubic meters)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Measurement'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Concrete Volume'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation for a concrete slab with dimensions 5 meters (length) × 4 meters (width) × 0.3 meters (depth):
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Length: {exampleData.length} meters</li>
              <li>Width: {exampleData.width} meters</li>
              <li>Depth: {exampleData.depth} meters</li>
              <li>Volume: {exampleData.volume} cubic meters</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Calculate volume:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Volume = Length × Width × Depth</li>
                  <li>Volume = {exampleData.length} × {exampleData.width} × {exampleData.depth} = {exampleData.volume} cubic meters</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Concrete Volume Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Concrete Volume Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Project Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Measure the length, width, and depth of the concrete slab accurately</li>
                <li>Ensure all measurements are precise</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Project Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the length in meters</li>
                <li>Input the width in meters</li>
                <li>Input the depth in meters</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated volume</li>
                <li>Use the bar chart for visual analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is the formula to calculate concrete volume?</h3>
              <p className="text-gray-600">
                The formula to calculate concrete volume is: Volume = Length × Width × Depth
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why is it important to calculate concrete volume accurately?</h3>
              <p className="text-gray-600">
                Accurate calculation of concrete volume is important because it:
                <ul className="list-disc pl-6 mt-2">
                  <li>Ensures you order the correct amount of concrete</li>
                  <li>Helps in budgeting and cost estimation</li>
                  <li>Prevents wastage of materials</li>
                  <li>Ensures the structural integrity of the project</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What if I don't know the exact depth of the concrete slab?</h3>
              <p className="text-gray-600">
                If you don't know the exact depth of the concrete slab, you can:
                <ol className="list-decimal pl-6 mt-2">
                  <li>Estimate the depth based on similar projects</li>
                  <li>Consult with a construction professional</li>
                  <li>Use an average depth for an approximate calculation</li>
                </ol>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Can I use this calculator for other construction projects?</h3>
              <p className="text-gray-600">
                Yes, this calculator can be used for other construction projects such as:
                <ul className="list-disc pl-6 mt-2">
                  <li>Concrete footings</li>
                  <li>Concrete walls</li>
                  <li>Concrete columns</li>
                  <li>Any project requiring volume calculation of concrete</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why are my results different from expected?</h3>
              <p className="text-gray-600">
                If your results seem unexpected, check:
                <ul className="list-disc pl-6 mt-2">
                  <li>If you've entered the correct dimensions</li>
                  <li>If your measurements are accurate</li>
                  <li>If all values are in the correct units</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Is concrete volume affected by
              <ul className="list-disc pl-6 mt-2">
                  <li>If you've entered the correct dimensions</li>
                  <li>If your measurements are accurate</li>
                  <li>If all values are in the correct units</li>
                </ul>
              </h3> 
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Is concrete volume affected by temperature?</h3>
              <p className="text-gray-600">
                While the volume of concrete itself is not significantly affected by temperature, the curing process and final strength of the concrete can be influenced by temperature variations. It's important to consider temperature when planning and executing concrete pours.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">Interactive bar charts for easy visualization of concrete volume</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
              <p className="text-gray-600">Accurate volume calculations with step-by-step breakdown</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
              <p className="text-gray-600">Automatic handling of measurement units</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Detailed explanations of volume calculation concepts</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
              <p className="text-gray-600">Intuitive interface with clear input fields</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
              <p className="text-gray-600">Instant calculation results as you type</p>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-semibold mb-4">Key Benefits</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Save time with automatic calculations</li>
              <li>Reduce calculation errors with built-in validation</li>
              <li>Learn volume calculation concepts through practical examples</li>
              <li>Accessible from any device</li>
              <li>Free to use for educational and professional purposes</li>
              <li>Perfect for construction professionals and DIY enthusiasts</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Concrete Volume <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Calculating the volume of concrete is essential for planning construction projects and ensuring the correct amount of material is ordered. Here's why it's important:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Accurate Material Estimation:</span> Ensuring you order the correct amount of concrete
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Cost Efficiency:</span> Avoiding over-purchasing and minimizing waste
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Project Management:</span> Planning and scheduling concrete pours effectively
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Structural Integrity:</span> Ensuring the correct volume for the required strength and support
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Always double-check your measurements and calculations to ensure accuracy and avoid costly mistakes.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default ConcreteVolumeCalculator;